/**
 * Loads every user profile the Users Management list shows, page by page.
 */
import { coreAPI, UserProfile } from '../../lib/api';

/** View-context scope (CR-5374) that narrows the Customer / Supplier cohorts. */
export interface UserListScope {
  view_level?: string;
  group?: number | null;
  company?: number | null;
  site?: number | null;
}

/**
 * Fetch all user profiles for ``scope``, following pagination to the end.
 *
 * The backend caps page_size at 100 (StandardResultsSetPagination
 * .max_page_size), so a single request silently truncated tenants with more
 * users — everyone past the cap vanished from the page, unfindable even by
 * search (all filtering here is client-side).
 *
 * @param scope - active view-context scope; omitted keys mean no narrowing.
 * @returns Every profile across all pages (stops after 50 pages).
 */
export async function loadUserProfiles(scope?: UserListScope): Promise<UserProfile[]> {
  const params: Record<string, string | number | boolean> = { page_size: 1000 };
  if (scope?.view_level) params['view_level'] = scope.view_level;
  if (scope?.group) params['group'] = scope.group;
  if (scope?.company) params['company'] = scope.company;
  if (scope?.site) params['site'] = scope.site;
  const all: UserProfile[] = [];
  for (let page = 1; page <= 50; page += 1) {
    const response = await coreAPI.getUserProfiles({ ...params, page });
    all.push(...(response.data.results || []));
    if (!response.data.next) break;
  }
  return all;
}
